/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../reducers';
import { useHistory, useParams } from 'react-router-dom';
import { updateSurveyScope } from '../../../../actions/api';
import { loadScorecardsBySurveyId } from '../../../../actions/scorecard';
import { ROUTES } from '../../../../constants/routes';
import { UtrvStatus } from '../../../../constants/status';
import { CustomScope } from '../../../../types/initiative';
import { generateUrl } from '../../../../routes/util';
import { AnalyticsEvents } from '../../../../services/analytics/AnalyticsEvents';
import { getAnalytics } from '../../../../services/analytics/AnalyticsService';
import { addSiteAlert } from '../../../../slice/siteAlertsSlice';
import { ScopeQuestionGroupOptionalValue } from '../../../../types/survey';
import Loader from '../../../loader';
import { processScopeData } from '../../../survey-scope/scopeSelection';
import { SurveyContext, SurveyContextLoadedProps } from '../../../survey-container/SurveyContainer';
import { ViewValues } from '../../../survey-overview-sidebar/viewOptions';
import QuestionProgress from './QuestionProgress';
import { getGroup } from '@g17eco/core';
import { isUserManagerByInitiativeId } from '../../../../selectors/user';
import { convertToFilters } from '../../utils/useScopeFilters';
import { useRecommendedAddons } from '../../../../hooks/app/useRecommendedAddons';
import { FlickityWrapper } from '../../../flickity-wrapper';
import EditButton from '../../../button/EditButton';
import { QuestionProgressEditModal } from './QuestionProgressEditModal';
import { ScopeWheelPreferences } from '../../../../model/surveyData';
import './styles.scss';
import { getQuestionGroupId } from '../../../survey-question-list/utils';
import { QUESTION } from '../../../../constants/terminology';

interface QuestionProgressContainerProps {
  surveyGroups: ScopeQuestionGroupOptionalValue[];
  config?: CustomScope[];
  onClickScopeWheel: (scopeCode: string) => void;
  readOnly?: boolean;
}

interface ProgressCircle {
  groupCode: string | undefined;
  groupName: string | JSX.Element;
  groupNameStr: string;
  totalCount?: number;
  completed: number | undefined;
  colour?: string | undefined;
  className?: string;
  onClick?: () => void;
  isVisible: boolean;
}

const getGroups = (surveyGroups: ScopeQuestionGroupOptionalValue[], sortCodes: string[]) => {
  let scopeGroups: ScopeQuestionGroupOptionalValue[] = [];

  scopeGroups = surveyGroups
    .filter(group => group.list.length !== 0)

  if (sortCodes.length > 0) {
    scopeGroups = scopeGroups.sort((a, b) => {
        // Sort in the same way that it was requested?
        const posA = sortCodes.indexOf(a.groupCode ?? '') ?? 0;
        const posB = sortCodes.indexOf(b.groupCode ?? '') ?? 0;
        return posA > posB ? 1 : posB > posA ? -1 : 0;
      });
  }
  return scopeGroups;
}

const getShortName = (group: ScopeQuestionGroupOptionalValue): string => {
  const code = group.groupCode;
  const coreGroup = code ? getGroup('standards-and-frameworks', code) : undefined;
  return coreGroup?.shortName ?? group.groupName;
}

const createProgressCircle = ({
  group,
  onClickScopeWheel,
  surveyGroups
}: {
  group: ScopeQuestionGroupOptionalValue;
  onClickScopeWheel: (scopeCode: string) => void;
  surveyGroups:  ScopeQuestionGroupOptionalValue[];
}): ProgressCircle => {
  const questionCompleted = group.list.filter((question) => question.utrv?.status === UtrvStatus.Verified);
  const completedPercentage = (questionCompleted.length / group.list.length) * 100;
  const groupIndex = surveyGroups.indexOf(group);

  return {
    groupCode: group.groupCode,
    groupName: getShortName(group),
    groupNameStr: getShortName(group),
    totalCount: group.list.length,
    completed: completedPercentage,
    colour: group.groupData?.colour,
    isVisible: true,
    onClick: () => onClickScopeWheel(getQuestionGroupId(groupIndex)),
  };
};

const QuestionProgressContainer = (props: QuestionProgressContainerProps) => {
  const { surveyGroups, config, readOnly, onClickScopeWheel } = props;

  const {
    surveyData,
    reloadSurvey,
    breadcrumbs,
    materiality,
    sidebarSettings: { groupBy },
  } = useContext(SurveyContext) as SurveyContextLoadedProps;

  const view = groupBy[0] as ViewValues;
  const { initiativeId, surveyId } = useParams<{ initiativeId: string, surveyId: string }>();
  const [isLoading, setLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const isManager = useAppSelector(state => isUserManagerByInitiativeId(state, initiativeId));

  const recommendedAddons = useRecommendedAddons()

  const dispatch = useAppDispatch();
  const history = useHistory();
  const handleAddAnother = useCallback(() => {
    history.push(
      generateUrl(ROUTES.COMPANY_TRACKER_SURVEY, {
        initiativeId,
        surveyId,
        page: 'scope'
      })
    );
  }, [history, initiativeId, surveyId]);

  const updateScope = useCallback((code: string) => {
    setLoading(true);
    const data = [code];
    const scopeType = ViewValues.StandardsAndFrameworks;
    const filters = convertToFilters(view, breadcrumbs);
    const scopeGroups = processScopeData(scopeType, data, filters, materiality)

    updateSurveyScope(surveyData._id, scopeGroups)
      .then(() => {
        const analytics = getAnalytics();
        return analytics.track(AnalyticsEvents.SurveyScopeUpdated, {
          initiativeId: surveyData.initiativeId,
          surveyId: surveyData._id,
          data,
          scopeType
        });
      })
      .then(() => reloadSurvey())
      .then(() => {
        dispatch(loadScorecardsBySurveyId(surveyData.initiativeId, surveyData._id, false, undefined, true));
        setLoading(false);
      })
      .catch((e: Error) => {
        dispatch(addSiteAlert({
          content: e.message,
          timeout: 5000
        }));
        setLoading(false);
      });
  }, [breadcrumbs, dispatch, reloadSurvey, materiality, surveyData, view]);

  const questionProgressList: ProgressCircle[] = useMemo(() => {
    const requiredScope = config?.filter(c => c.required).map(c => c.code) ?? [];
    const recommendedScope = recommendedAddons.map(addon => addon.code);
    const inScope: string[] = surveyData.scope ?
      Object.values(surveyData.scope)
        .reduce(
          (acc: string[], s: string[]) => (
            [
              ...acc,
              // Add all things in scope that are not already covered by the required/recommended selection
              ...s.filter(code => !requiredScope.includes(code) && !recommendedScope.includes(code))
            ]
          ), []) : [];

    // required/recommended comes first in the circles list
    const requiredCodes =  [...requiredScope, ...recommendedScope];
    // sort and filter
    const requiredGroups = getGroups(surveyGroups, requiredCodes).filter(group => group.groupCode && requiredCodes.includes(group.groupCode))
    const requiredCircles = requiredGroups.map((group) => createProgressCircle({ group, surveyGroups, onClickScopeWheel }));

    // filter out required groups above
    const inScopeCircles = getGroups(surveyGroups, inScope)
      .filter(group => !requiredGroups.some(el => el.groupCode === group.groupCode))
      .map((group) => createProgressCircle({ group, surveyGroups, onClickScopeWheel }));

    if (!isManager) {
      // Only display required and inScope, as updateScope is not possible
      return [...requiredCircles, ...inScopeCircles]
    }

    const addRecommended: ProgressCircle[] = readOnly ? [] : recommendedAddons
      .filter(addon => !requiredCircles.map(g => g.groupCode).includes(addon.code))
      .map(addon => ({
        groupCode: `add_${addon.code}`,
        groupName: <><i className='fa fa-plus mr-2' />Add {addon.name}</>,
        groupNameStr: `Add ${addon.name}`,
        logo: addon.logo,
        completed: 0,
        className: 'light',
        onClick: isLoading ? undefined : () => updateScope(addon.code),
        isVisible: true,
      }));

    const progressList = [...requiredCircles, ...addRecommended, ...inScopeCircles]

    // Re-order
    const scopeWheelPreferences = surveyData.displayPreferences?.scopeWheels ?? [];

    const orderedProgressList: ProgressCircle[] = [];
    scopeWheelPreferences.forEach(scopeWheelPreference => {
      const findWheel = progressList.find(wheel => wheel.groupCode === scopeWheelPreference.scopeCode);
      if (!findWheel) {
        return;
      }
      orderedProgressList.push({
        ...findWheel,
        isVisible: scopeWheelPreference.visible !== false
      });
      const findWheelIx = progressList.findIndex(wheel => wheel.groupCode === scopeWheelPreference.scopeCode);
      progressList.splice(findWheelIx, 1);
    });

    progressList.forEach(wheel => orderedProgressList.push(wheel)) // The rest

    const isSurveyComplete = Boolean(surveyData?.completedDate);
    if (!isSurveyComplete && !readOnly) {
      orderedProgressList.push({
        groupCode: 'add_another',
        groupName: <><i className='fa fa-plus mr-2' />Add another pack</>,
        groupNameStr: 'Add another pack',
        completed: undefined,
        className: 'light',
        onClick: handleAddAnother,
        isVisible: true
      });
    }

    return orderedProgressList;
  }, [
    config,
    recommendedAddons,
    surveyData.scope,
    surveyData?.completedDate,
    surveyGroups,
    isManager,
    handleAddAnother,
    isLoading,
    updateScope,
    surveyData.displayPreferences?.scopeWheels,
    onClickScopeWheel,
    readOnly,
  ]);

  const scopeWheelPreferences: ScopeWheelPreferences[] = useMemo(() => {
    const scopeOrderVisibility = new Map<string, { isVisible: boolean, inScope: boolean, name: string }>();
    surveyData.displayPreferences?.scopeWheels?.forEach((d) => {
      scopeOrderVisibility.set(d.scopeCode, {
        name: d.scopeCode,
        isVisible: d.visible,
        inScope: false
      });
    });

    questionProgressList.forEach(group => {
      const scopeCode = group.groupCode;
      if (!scopeCode) {
        return;
      }
      const currentOrderVisibility = scopeOrderVisibility.get(scopeCode);
      if (currentOrderVisibility) {
        scopeOrderVisibility.set(scopeCode, {
          ...currentOrderVisibility,
          name: group.groupNameStr,
          inScope: true
        });
      } else {
        scopeOrderVisibility.set(scopeCode, {
          name: group.groupNameStr,
          isVisible: true,
          inScope: true
        });
      }
    });

    return Array.from(scopeOrderVisibility)
      .filter(([scopeCode]) => scopeCode !== 'add_another')
      .filter(([, { inScope }]) => inScope)
      .map(([scopeCode, { isVisible, name }]) => ({
        scopeCode,
        name,
        visible: isVisible
      }));
  }, [surveyData.displayPreferences?.scopeWheels, questionProgressList]);

  return (
    <div className='question-progress-container' style={{ opacity: isLoading ? 0.5 : 1 }}>
      {isLoading ? <Loader /> : null}
      <div className='d-flex justify-content-between'>
        <h5 className='mb-4 mt-3'>{QUESTION.CAPITALIZED_SINGULAR} progress</h5>
        {isManager ? <div>
          <EditButton tooltip={`Edit ${QUESTION.SINGULAR} progress wheels`} active={showEditModal} onClick={() => setShowEditModal(true)} />
        </div> : null}
      </div>
      {questionProgressList.length === 0 ? null :
        <div className='flickity-container mb-5'>
          <FlickityWrapper
            disableImagesLoaded={true}
            reloadOnUpdate={true}
            options={{
              cellAlign: 'left',
              pageDots: true,
              draggable: true,
              groupCells: true,
            }}
          >
            {questionProgressList
              .filter((group) => group.isVisible)
              .map((group) => (
                <QuestionProgress
                  key={String(group.groupCode ?? group.groupName)}
                  {...group}
                  disabled={isLoading || Boolean(surveyData.completedDate)}
                />
              ))}
          </FlickityWrapper>
        </div>
      }
      {showEditModal ? <QuestionProgressEditModal surveyId={surveyId} handleReload={reloadSurvey} scopeWheelPreferences={scopeWheelPreferences} toggle={() => setShowEditModal(false)} /> : null}
    </div>
  )
}

export default QuestionProgressContainer;
